import { useState } from 'react'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Card } from '@/shared/ui/Card'
import { FilterBar } from '@/shared/ui/FilterBar'
import { Avatar } from '@/shared/ui/Avatar'
import { RotateCcw, Loader2 } from 'lucide-react'
import { useDebounce } from '@/shared/hooks'
import { useDeletedUsers, useRestoreUser } from '@/api/hooks'

interface U { id:number; name?:string; email?:string; phone?:string; user_type?:string; created_at?:string; deleted_at?:string }

function formatDateTime(value?: string | null) {
  if (!value) return '—'
  // The API sends "2026-09-20 22:42:53" — a space, not the "T" ISO parsing
  // needs — so `new Date(value)` as-is returns Invalid Date in Safari/Firefox.
  const d = new Date(String(value).replace(' ', 'T'))
  if (isNaN(d.getTime())) return '—'
  return `${d.toLocaleDateString('en-GB').replace(/\//g,'-')} ${d.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit',hour12:true})}`
}

export function UserTrackingPage() {
  const [search, setSearch] = useState('')
  const debounced = useDebounce(search)

  const q = useDeletedUsers({ search: debounced || undefined })

  const restore = useRestoreUser()

  const rows: U[] = q.data?.data ?? q.data ?? []

  return (
    <div className="space-y-5">
      <PageHeader title="Delete Users" subtitle="Restore previously deleted accounts"
        breadcrumbs={[{label:'Dashboard',to:'/dashboard'},{label:'Delete Users'}]}/>

      <Card padding={false}>
        <div className="p-5 border-b border-gray-100">
          <FilterBar search={{ value:search, onChange:setSearch, placeholder:'Search users…' }}/>
        </div>
        {q.isError && <p className="px-5 py-4 text-sm text-red-500">Failed to load users.</p>}
        {q.isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-brand-500" size={24}/></div>
        ) : (
          <div className="overflow-x-auto">
          <table className="drivys-table w-full">
            <thead className="bg-gray-50"><tr><th>User</th><th>Email</th><th>Phone</th><th>Type</th><th>Created At</th><th>Deleted At</th><th>Actions</th></tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-gray-400 py-12 text-sm">No deleted users found</td></tr>
              ) : rows.map(u => (
                <tr key={u.id}>
                  <td><div className="flex items-center gap-2"><Avatar name={u.name ?? 'U'} size="xs"/><span className="font-medium text-gray-900">{u.name ?? '—'}</span></div></td>
                  <td className="text-gray-600 text-sm">{u.email ?? '—'}</td>
                  <td className="text-gray-600 text-sm">{u.phone ?? '—'}</td>
                  <td className="text-gray-500 text-xs uppercase">{u.user_type ?? '—'}</td>
                  <td className="text-gray-600 text-sm">{formatDateTime(u.created_at)}</td>
                  <td className="text-gray-600 text-sm">{formatDateTime(u.deleted_at)}</td>
                  <td>
                    <button onClick={()=>restore.mutate(u.id)} disabled={restore.isPending}
                      className="inline-flex items-center gap-1 px-2.5 h-7 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 text-xs font-medium disabled:opacity-50"><RotateCcw size={12}/> Restore</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>
    </div>
  )
}
